
import React from 'react';
import { Button } from '@/components/ui/button';
import { Shield, MapPin, Users, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

interface HeroSectionProps {
  currentLang: string;
}

const HeroSection = ({ currentLang }: HeroSectionProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="container mx-auto text-center max-w-5xl">
        {/* Main Heading with animation - reduced from text-8xl md:text-9xl */}
        <h1 className="text-6xl md:text-7xl font-bold text-primary mb-6 animate-fade-in tracking-tight">
          {t('maskan', 'مسكن', 'Maskan')}
        </h1>
        
        {/* Subheading with accent color - reduced from text-3xl md:text-4xl */}
        <p className="text-2xl md:text-3xl text-muted-foreground mb-16 font-medium animate-fade-in">
          {t('hero_subtitle', 'سكن طلابي موثوق في مصر', 'Trusted Student Housing in Egypt')}
        </p>
        
        {/* Action Buttons with proper spacing and animations - reduced text size */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-20">
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary-hover text-white px-10 py-3 text-lg rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-fade-in"
            onClick={() => navigate('/search')}
          >
            {t('find_housing', 'ابحث عن سكن', 'Find Housing')}
          </Button>
          <Button 
            size="lg" 
            className="bg-[#C8A882] text-white hover:bg-[#B8986F] hover:text-white px-10 py-3 text-lg rounded-xl font-semibold transition-all duration-300 hover:scale-105 animate-fade-in"
            onClick={() => navigate('/list-property')}
          >
            {t('list_property', 'أضف عقارك', 'List Your Property')}
          </Button>
        </div>
        
        {/* Stats with proper spacing and icons - reduced text size */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-12 mb-24 text-muted-foreground animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center">
              <div className="w-2 h-2 bg-current rounded-full"></div>
            </div>
            <span className="text-base font-medium">{t('verified_100', 'متحقق 100%', '100% Verified')}</span>
          </div>
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5" />
            <span className="text-base font-medium">{t('students_count', '+10,000 طالب', '10,000+ Students')}</span>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5" />
            <span className="text-base font-medium">{t('universities_count', '+50 جامعة', '50+ Universities')}</span>
          </div>
        </div>
        
        {/* Feature Cards with enhanced design - reduced text sizes */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fade-in">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Shield className="h-7 w-7 text-primary" />
              </div>
            </div>
            <h3 className="font-bold text-foreground mb-2 text-base">{t('verified_listings', 'قوائم متحققة', 'Verified Listings')}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{t('verified_listings_desc', 'جميع العقارات والملاك متحققون', 'All properties and owners verified')}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fade-in">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
                <MapPin className="h-7 w-7 text-amber-600" />
              </div>
            </div>
            <h3 className="font-bold text-foreground mb-2 text-base">{t('university_focused', 'مرتكز على الجامعات', 'University Focused')}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{t('university_focused_desc', 'ابحث حسب المسافة من الحرم الجامعي', 'Search by distance from campus')}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fade-in">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Users className="h-7 w-7 text-blue-600" />
              </div>
            </div>
            <h3 className="font-bold text-foreground mb-2 text-base">{t('student_community', 'مجتمع طلابي', 'Student Community')}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{t('student_community_desc', 'سكن آمن للطلاب', 'Safe housing for students')}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fade-in">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center">
                <Clock className="h-7 w-7 text-green-600" />
              </div>
            </div>
            <h3 className="font-bold text-foreground mb-2 text-base">{t('quick_response', 'استجابة سريعة', 'Quick Response')}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{t('quick_response_desc', 'استجابة سريعة من المالكين', 'Fast owner responses')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
